import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';
import { TenantFeatures, TenantFeatureKey, DEFAULT_TENANT_FEATURES, TenantFeatureKeys } from './tenant-feature.model';
import { TenantConfig } from './tenant-config.model';
import { environment } from '../../../environments/environment';

/**
 * Service zum Laden und Abfragen von Feature-Toggles pro Tenant.
 *
 * Features werden beim App-Start aus der TenantConfig initialisiert
 * (via `initialisierenAusConfig()`). Ueber `istAktiv()` kann jede
 * Komponente pruefen ob ein Feature verfuegbar ist.
 *
 * Der separate Endpoint PUT /api/tenant/features wird nur vom Admin-UI
 * zum Aktualisieren der Features verwendet.
 */
@Injectable({ providedIn: 'root' })
export class TenantFeatureService {

  private readonly FEATURES_URL = `${environment.apiUrl}/api/tenant/features`;
  private readonly http = inject(HttpClient);

  /** Aktuelle Features als Observable. */
  readonly features$ = new BehaviorSubject<TenantFeatures>(DEFAULT_TENANT_FEATURES);

  /**
   * Initialisiert die Features aus der bereits geladenen TenantConfig.
   * Wird vom TenantConfigService nach dem Laden der Config aufgerufen.
   * Fehlende Keys werden mit den Default-Werten ergaenzt.
   *
   * @param config Die geladene TenantConfig.
   */
  initialisierenAusConfig(config: TenantConfig): void {
    if (config.features) {
      const merged = this.mitLegacyFeatureKeys({
        ...DEFAULT_TENANT_FEATURES,
        ...config.features,
      });
      this.features$.next(merged);
    }
  }

  /**
   * Laedt die Features vom Backend (fuer Refresh nach Admin-Aenderungen).
   * Bei Fehler werden die Default-Features verwendet.
   *
   * @returns Observable<TenantFeatures>
   */
  laden(): Observable<TenantFeatures> {
    return this.http.get<TenantFeatures>(this.FEATURES_URL).pipe(
      catchError(() => {
        console.warn('[TenantFeatureService] API nicht erreichbar – Default-Features werden verwendet.');
        return of(DEFAULT_TENANT_FEATURES);
      }),
      tap(features => {
        const merged = { ...DEFAULT_TENANT_FEATURES, ...features };
        this.features$.next(merged);
      })
    );
  }

  /**
   * Prueft ob ein Feature fuer den aktuellen Tenant aktiviert ist.
   * Synchrone Methode fuer Guards, Sidebar, Tabs etc.
   *
   * @param feature Der Feature-Key der geprueft werden soll.
   * @returns true wenn das Feature aktiv ist, sonst false.
   */
  istAktiv(feature: TenantFeatureKey): boolean {
    const features = this.features$.getValue();
    return features[feature] ?? DEFAULT_TENANT_FEATURES[feature] ?? false;
  }

  /**
   * Speichert aktualisierte Features im Backend.
   * Aktualisiert auch den lokalen State.
   *
   * @param features Die zu speichernden Feature-Toggles.
   * @returns Observable<void>
   */
  speichern(features: TenantFeatures): Observable<void> {
    return this.http.put<void>(this.FEATURES_URL, features).pipe(
      tap(() => {
        const merged = this.mitLegacyFeatureKeys({
          ...DEFAULT_TENANT_FEATURES,
          ...features,
        });
        this.features$.next(merged);
      })
    );
  }

  /** Bis alle Umgebungen migriert sind: API-Key `tagebuch` → `projektberichte`. */
  private mitLegacyFeatureKeys(features: TenantFeatures): TenantFeatures {
    const m = { ...features } as Record<string, boolean>;
    const legacy = m['tagebuch'];
    if (legacy !== undefined && m[TenantFeatureKeys.Projektberichte] === undefined) {
      m[TenantFeatureKeys.Projektberichte] = legacy;
    }
    delete m['tagebuch'];
    return m as TenantFeatures;
  }
}
